import type { Deal, DealChange } from "./api";
import { cycleWeeks, recommendedSize } from "./calculators";

export const STEPS = ["site", "volume", "terms", "agreement", "service"] as const;
export type Step = (typeof STEPS)[number];
export type DealStatus = "draft" | "submitted" | "approved" | "rejected" | "won";
export type DealView = "open" | "approve" | "won";

/** A list load is shown only on the tab that asked for it; until then the tab loads. */
export function viewLoad<T>(loaded: { view: DealView; load: T } | undefined, view: DealView): T | undefined {
  return loaded?.view === view ? loaded.load : undefined;
}

export const STEP_TEXT: Record<Step, string> = {
  site: "Site and contacts",
  volume: "Volume and equipment",
  terms: "Terms",
  agreement: "Agreement",
  service: "Service setup",
};

export const STATUS_TEXT: Record<DealStatus, string> = {
  draft: "Draft",
  submitted: "Waits for approval",
  approved: "Approved",
  rejected: "Rejected",
  won: "Won",
};

export const OIL_TEXT = { soybean: "Soybean", canola: "Canola", clear: "Clear", creamy: "Creamy", animal: "Animal fat", solid: "Solid" };
export const ACCESSORY_TEXT = { transit_cart: "Transit cart", mount: "Mount", lock: "Lock", filter_cart: "Filter cart", adapters: "Adapters" };
export const METHOD_TEXT = { rate: "$ per gallon", percent: "% of the market value", flat: "Flat $ each period" };
export type Method = keyof typeof METHOD_TEXT;
export const TERMS = [12, 24, 36] as const;

type Kind = "text" | "note" | "int" | "decimal" | "percent" | "rebate" | "date" | "time" | "choice" | "number-choice" | "check" | "multi" | "state";

export interface FieldSpec {
  key: keyof DealChange & string;
  label: string;
  kind: Kind;
  max?: number;
  min?: number;
  hint?: string;
  options?: Record<string, string>;
}

const text = (key: FieldSpec["key"], label: string, max: number, hint?: string): FieldSpec => ({ key, label, kind: "text", max, hint });

/** The fields of each step of the deal form (design 9.4). Choices that come from the server are empty here. */
export const FIELDS: Record<Step, FieldSpec[]> = {
  site: [
    text("site_name", "Business name", 200),
    text("address", "Address", 300),
    { key: "state", label: "State", kind: "state", hint: "Two letters, for example PA. It chooses the account number range." },
    text("phone", "Phone", 40),
    text("hours", "Opening hours", 200),
    text("contact_name", "Contact name", 200),
    text("contact_role", "Contact role", 100, "For example: owner, kitchen manager."),
    text("contact_phone", "Contact phone", 40),
    text("contact_email", "Contact email", 200),
    text("payee_name", "Payee (who gets the rebate check)", 200, "Often a different company from the site, for example a franchisee's company."),
    text("payee_address", "Payee mail address", 300),
  ],
  volume: [
    { key: "fryers", label: "Fryers", kind: "int", min: 0, max: 100 },
    { key: "changes_per_week", label: "Oil changes each week", kind: "decimal", min: 0, max: 21 },
    { key: "fryer_lb", label: "Fryer size (lb)", kind: "decimal", min: 0, max: 500, hint: "A 35 lb jug is 4.6 gal." },
    { key: "oil_type", label: "Oil type", kind: "choice", options: OIL_TEXT },
    { key: "grill_share", label: "Grill or smoker grease (%)", kind: "percent", min: 0, max: 100 },
    { key: "rate_gal", label: "Stated rate (gal each week)", kind: "decimal", min: 0.01, max: 10_000, hint: "The raw gallons. The calculator below gives a value." },
    { key: "week_group", label: "Weeks of the visits", kind: "choice", hint: "The container size needs the weeks between visits. Step 5 shows it too." },
    { key: "container_size", label: "Container size (gal)", kind: "number-choice" },
    { key: "accessories", label: "Accessories", kind: "multi", options: ACCESSORY_TEXT },
  ],
  terms: [
    { key: "rebate_method", label: "Rebate method", kind: "choice", options: METHOD_TEXT },
    { key: "rebate_value", label: "Rebate", kind: "rebate", min: 0, max: 100_000 },
    text("consideration", "Added consideration", 300, "For example: a sign-on payment, a gift card, or a transit cart."),
    { key: "consideration_amount", label: "Added consideration ($)", kind: "decimal", min: 0, max: 1_000_000 },
    { key: "term_months", label: "Term", kind: "number-choice", options: { 12: "12 months", 24: "24 months", 36: "36 months" } },
    { key: "auto_renew", label: "Renews automatically", kind: "check" },
    { key: "notice_days", label: "Notice period (days)", kind: "int", min: 0, max: 365 },
    text("previous_collector", "Previous collector", 200),
  ],
  agreement: [
    text("signer_name", "Signer name", 200),
    text("signer_title", "Signer title", 100),
    { key: "signed_on", label: "Signed on", kind: "date" },
  ],
  service: [
    { key: "delivery_on", label: "Container delivery", kind: "date" },
    { key: "service_start", label: "Service starts", kind: "date" },
    { key: "area_id", label: "Area", kind: "choice" },
    { key: "week_group", label: "Weeks of the visits", kind: "choice" },
    { key: "window_start", label: "Service window from", kind: "time" },
    { key: "window_end", label: "Service window to", kind: "time" },
    { key: "access_note", label: "Access notes", kind: "note", max: 1000 },
  ],
};

const FIELD_LABEL: Record<string, string> = Object.fromEntries(
  STEPS.flatMap((step) => FIELDS[step].map((field) => [field.key, field.label.replace(/ \(.*\)$/, "")])),
);

/** "Payee, Payee mail address": the labels of the fields that a step still needs. */
export const missingText = (fields: readonly string[]) => fields.map((field) => FIELD_LABEL[field] ?? field).join(", ");

/** The contract and payment terms: only a role with a contract grant sets them (rule Q-14). */
export const TERMS_FIELDS = [
  "payee_name",
  "payee_address",
  "rebate_method",
  "rebate_value",
  "consideration",
  "consideration_amount",
  "term_months",
  "auto_renew",
  "notice_days",
] as const;

export type FormValue = string | boolean | string[];
export type FormValues = Record<string, FormValue>;

const asText = (value: unknown) => (value === null || value === undefined ? "" : String(value));

/** The value of one field as the form shows it. A percent shows as 0 to 100. */
export function fieldText(deal: Deal, field: FieldSpec): FormValue {
  const value = deal[field.key as keyof Deal];
  if (field.kind === "check") return Boolean(value);
  if (field.kind === "multi") return Array.isArray(value) ? [...value] : [];
  if (value === null || value === undefined) return "";
  if (field.kind === "percent" || (field.kind === "rebate" && deal.rebate_method === "percent")) return String(Math.round(Number(value) * 10_000) / 100);
  return asText(value);
}

export const formValues = (deal: Deal, step: Step): FormValues => Object.fromEntries(FIELDS[step].map((field) => [field.key, fieldText(deal, field)]));

export type FormResult = { change: DealChange } | { field: string; text: string };

function numberOf(field: FieldSpec, raw: string, scale = 1): number | null | string {
  const trimmed = raw.trim().replace(/,/g, "");
  if (trimmed === "") return null;
  const value = Number(trimmed);
  const low = field.min ?? 0;
  const high = field.max ?? Number.MAX_SAFE_INTEGER;
  if (!Number.isFinite(value) || value < low || value > high) return `${field.label}: give a number from ${low.toLocaleString("en-US")} to ${high.toLocaleString("en-US")}.`;
  if (field.kind === "int" && !Number.isInteger(value)) return `${field.label}: give a whole number.`;
  return value / scale;
}

const NOT_NULL: Partial<Record<string, string>> = { notice_days: "Give the notice period in days.", consideration_amount: "" };

/** The fields of the step that differ from the deal, as the API takes them, or the first problem. */
export function formChange(deal: Deal, step: Step, values: FormValues, method: string): FormResult {
  const change: Record<string, unknown> = {};
  for (const field of FIELDS[step]) {
    const raw = values[field.key];
    let value: unknown;
    if (field.kind === "check") value = Boolean(raw);
    else if (field.kind === "multi") value = [...((raw as string[]) ?? [])].sort();
    else {
      const given = typeof raw === "string" ? raw : "";
      if (["int", "decimal", "percent", "rebate"].includes(field.kind)) {
        const percent = field.kind === "percent" || (field.kind === "rebate" && method === "percent");
        const found = numberOf(percent ? { ...field, max: 100 } : field, given, percent ? 100 : 1);
        if (typeof found === "string") return { field: field.key, text: found };
        if (found === null && field.key in NOT_NULL) {
          if (NOT_NULL[field.key]) return { field: field.key, text: NOT_NULL[field.key] as string };
          value = 0;
        } else value = found;
      } else if (field.kind === "number-choice") value = given ? Number(given) : null;
      else if (field.kind === "state") {
        value = given.trim().toUpperCase();
        if (value && !/^[A-Z]{2}$/.test(value as string)) return { field: field.key, text: "The state is two letters, for example PA." };
      } else if (["date", "time", "choice"].includes(field.kind)) value = given || null;
      else {
        value = given.trim();
        const problem = textProblem(field, value as string);
        if (problem) return { field: field.key, text: problem };
      }
    }
    const before = deal[field.key as keyof Deal];
    const same = Array.isArray(value) ? JSON.stringify(value) === JSON.stringify([...((before as string[]) ?? [])].sort()) : value === (before ?? (typeof value === "string" ? "" : null));
    if (!same) change[field.key] = value;
  }
  if (step === "service" && Boolean(values.window_start) !== Boolean(values.window_end)) {
    return { field: "window_end", text: "Give the start and the end of the service window, or neither." };
  }
  const [start, end] = [String(values.window_start ?? ""), String(values.window_end ?? "")];
  if (step === "service" && start && end && start >= end) {
    return { field: "window_end", text: "The service window must end after it starts." };
  }
  const [delivery, service] = [String(values.delivery_on ?? ""), String(values.service_start ?? "")];
  if (step === "service" && delivery && service && service < delivery) {
    return { field: "service_start", text: "The service starts on or after the delivery day." };
  }
  return { change: change as DealChange };
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^[0-9+().\-\s x]+$/;

/** The problem with one text value: too long, or not an email or a phone number. */
function textProblem(field: FieldSpec, value: string): string | null {
  if (field.max && value.length > field.max) return `${field.label}: ${field.max} characters at most. It has ${value.length}.`;
  if (!value) return null;
  if (field.key === "contact_email" && !EMAIL.test(value)) return "Give an email address, for example name@example.com.";
  const digits = value.replace(/\D/g, "").length;
  if (["phone", "contact_phone"].includes(field.key) && (!PHONE.test(value) || digits < 7)) {
    return `${field.label}: give a phone number with 7 or more digits.`;
  }
  return null;
}

export const money = (value: number) => value.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 4 });

/** "$0.10 per gal", "40% of the market value" or "$100.00 each period". */
export function rebateText(method: string | null, value: number | null): string {
  if (!method || value === null) return "–";
  if (method === "percent") return `${Math.round(value * 10_000) / 100}% of the market value`;
  return method === "rate" ? `${money(value)} per gal` : `${money(value)} each period`;
}

/** "$0.40/gal", "40%" or "$100/period": the short rebate for a table cell. */
export function rebateShort(method: string | null, value: number | null): string {
  if (!method || value === null) return "–";
  if (method === "percent") return `${Math.round(value * 10_000) / 100}%`;
  return method === "rate" ? `${money(value)}/gal` : `${money(value)}/period`;
}

/** A warning when the container does not suit the rate and the weeks between visits (design 8.6). */
export function sizeWarning(deal: Deal, sizes: readonly number[], margin: number): string | null {
  if (!deal.rate_gal || !deal.container_size) return null;
  if (!deal.week_group) return "Choose the weeks of the visits, so that the container size can be checked.";
  const weeks = cycleWeeks(deal.week_group);
  const found = recommendedSize(deal.rate_gal, weeks, margin, sizes);
  if (!found || found.sizes.length !== 1 || found.sizes[0] === deal.container_size) return null;
  return `The container is ${deal.container_size} gal, but ${weeks} ${weeks === 1 ? "week" : "weeks"} between visits need ${found.sizes[0]} gal. Change it in step 2.`;
}

/** The steps that still miss a field, with the count of each. */
export function openSteps(deal: Deal): [Step, number][] {
  return STEPS.map((step) => [step, deal.missing[step]?.length ?? 0] as [Step, number]).filter(([, count]) => count > 0);
}

/** The one action that the deal waits for, as the deal page shows it. */
export function nextAction(deal: Deal): string {
  if (deal.status === "won") return `Won. Account ${deal.account_number ?? ""} is made.`;
  if (deal.status === "submitted") return "Waits for a manager to approve the terms.";
  if (deal.status === "approved") return deal.unsigned.length ? "Approved. Enter the signer and the date in the step Agreement, then make the account." : "Approved. Make the account.";
  const open = openSteps(deal).length;
  if (open) return `Fill in ${open === 1 ? "1 more step" : `${open} more steps`}, then send it.`;
  return deal.status === "rejected" ? "Rejected. Change the terms or send it again." : "Ready to send.";
}

/** "October 21, 2026": a day in words, for a page that is printed. */
export function longDay(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
}

/** The paragraphs of the printed agreement. The text is a synthetic sample until counsel gives the real one. */
export function agreementText(deal: Deal, collector: string): string[] {
  const term = deal.term_months ? `${deal.term_months} months` : "[term]";
  const renew = deal.auto_renew ? `It renews automatically for the same term unless one party gives notice ${deal.notice_days} days before the end.` : "It does not renew automatically.";
  const extras = deal.accessories.length ? deal.accessories.map((item) => ACCESSORY_TEXT[item as keyof typeof ACCESSORY_TEXT] ?? item).join(", ") : "none";
  return [
    `This agreement is between ${collector} (the collector) and ${deal.site_name || "[business name]"}, ${deal.address || "[address]"} (the customer).`,
    `The term is ${term}, from the first day of service${deal.service_start ? `, ${longDay(deal.service_start)}` : ""}. ${renew}`,
    `The collector supplies a ${deal.container_size ? `${deal.container_size} gal` : "[size]"} container and these accessories: ${extras}. The collector collects the used cooking oil on its route.`,
    `The collector pays a rebate of ${rebateText(deal.rebate_method, deal.rebate_value)} on the net gallons to ${deal.payee_name || "[payee]"}, ${deal.payee_address || "[payee address]"}.`,
    ...(deal.consideration ? [`Added consideration: ${deal.consideration}${deal.consideration_amount ? ` (${money(deal.consideration_amount)})` : ""}.`] : []),
    "Sample text for tests only. Counsel supplies the real agreement text.",
  ];
}

export interface RangeRow {
  state: string;
  first: string;
  last: string;
}

/** The number ranges to save, or the first problem with its row (design 9.5, rule Q-13). */
export function rangesChange(rows: readonly RangeRow[]): { ranges: { state: string; first: number; last: number }[] } | { row: number; text: string } {
  const ranges: { state: string; first: number; last: number }[] = [];
  for (const [row, given] of rows.entries()) {
    if (!given.state.trim() && !given.first.trim() && !given.last.trim()) continue;
    const state = given.state.trim().toUpperCase();
    const [first, last] = [Number(given.first), Number(given.last)];
    if (!/^[A-Z]{2}$/.test(state)) return { row, text: "A state is two letters, for example PA." };
    if (![first, last].every((value) => Number.isInteger(value) && value >= 1 && value <= 999_999) || first > last) {
      return { row, text: "A range goes from a low whole number to a high one, from 1 to 999,999." };
    }
    const clash = ranges.find((other) => other.state === state || (first <= other.last && other.first <= last));
    if (clash) return { row, text: clash.state === state ? `${state} has two ranges.` : `The range overlaps the range of ${clash.state}.` };
    ranges.push({ state, first, last });
  }
  return { ranges };
}
